import { Button } from "@/components/ui/button";
import { useModal } from "@/context/ModalContext";
import { sendContactEmail } from "@/services/emails";
import { MonitorCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

interface TakyonServicesTemplateProps {
  title: string;
  subtitle: string;
  buttonText: string;
  buttonLink: { type: number; link: string; modalTxt?: string };
  buttonSubtext: string;
  descriptionTitle: string;
  descriptionText: string;
  features: { title: string; description: string }[];
  faq: { question: string; answer: string }[];
  image?: string;
}

export default function TakyonServicesTemplate({
  title,
  subtitle,
  buttonText,
  buttonLink,
  buttonSubtext,
  descriptionTitle,
  descriptionText,
  features,
  faq,
  image,
}: TakyonServicesTemplateProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { collectionId } = useParams();
  const { showModal } = useModal();
  const pathParts = window.location.pathname.split("/");
  const servicePath = pathParts.slice(-2).join("/");

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-16">
      {/* Back button */}
      <button onClick={() => navigate(-1)} className="text-sm text-gray-600">
        &larr; {t("collection.back_to_collection")}
      </button>

      {/* Title Section */}
      <div className="text-center space-y-11">
        <div className="w-10 h-10 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
          {image ? (
            <img src={image} alt="Service icon" className="w-6 h-6" />
          ) : (
            <MonitorCheck />
          )}
        </div>
        <h1 className="text-5xl font-medium">{title}</h1>
        <p className="text-gray-500 text-base font-medium">{subtitle}</p>
        <div className="flex flex-col items-center space-y-1">
          <Button
            variant={"takyon"}
            onClick={() => {
              switch (buttonLink.type) {
                case 0: // Open link
                  window.open(buttonLink.link, "_blank");
                  break;
                case 1: // Navigate to route
                  navigate(`/collection/${collectionId}/${buttonLink.link}`);
                  break;
                case 2: // Open modal
                  try {
                    sendContactEmail({
                      collectionId: collectionId,
                      type: servicePath,
                    });
                  } catch (error) {
                    console.error("Error sending contact email:", error);
                  }
                  showModal(
                    <div
                      dangerouslySetInnerHTML={{
                        __html: buttonLink.modalTxt || "",
                      }}
                    />
                  );
                  break;
              }
            }}
          >
            {buttonText}
          </Button>
          <span className="text-xs text-gray">{buttonSubtext}</span>
        </div>
      </div>

      {/* Main Image */}
      {/* <div
        style={{
          background: `url(${image})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
        }}
        className="border-2 rounded-2xl w-full h-80 bg-gray-200 mt-5"
      ></div> */}

      {/* Description Section */}
      <div className="text-center space-y-8">
        <p className="text-xs font-medium tracking-wide text-gray-400">
          {descriptionTitle}
        </p>
        <p className="text-gray-600">{descriptionText}</p>
      </div>

      {/* Features Grid */}
      <div className="flex justify-center">
        <div className="flex flex-wrap gap-10 py-7 justify-center">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white shadow-md rounded-xl p-4 text-center space-y-2 w-[220px]"
            >
              <h3 className="font-semibold">{feature.title}</h3>
              <p
                className="text-gray-500 text-sm"
                dangerouslySetInnerHTML={{ __html: feature.description }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="space-y-4">
        <h2 className="text-center font-semibold text-gray-600">FAQ</h2>
        {faq.map((item, index) => (
          <div key={index} className="border-b pb-4">
            <details className="group">
              <summary className="flex justify-between items-center cursor-pointer font-medium">
                {item.question}
                <span className="ml-2 group-open:rotate-45 transform transition-transform">
                  +
                </span>
              </summary>
              <div
                className="mt-2 text-gray-500 text-sm"
                dangerouslySetInnerHTML={{ __html: item.answer }}
              />
            </details>
          </div>
        ))}
      </div>

      <br />
      <br />
      <br />
      <br />
      <br />
    </div>
  );
}
